import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import BrandHeader from "../components/brand/BrandHeader";
import Footer from "../components/brand/Footer";
import { formatMoney } from "../lib/brand";
import { addToCart, useCart, cartCount } from "../lib/cart";
import { toast } from "sonner";
import { ShoppingBag, MagnifyingGlass, Storefront } from "@phosphor-icons/react";

function ProductCard({ p, onAdd }) {
  return (
    <div className="tsc-card overflow-hidden group transition-transform hover:-translate-y-0.5" data-testid={`shop-product-${p.id}`}>
      <Link to={`/shop/${p.id}`} className="block aspect-[4/3] bg-[#0a0a0a] overflow-hidden">
        {p.featured_photo ? (
          <img src={p.featured_photo} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30">
            <Storefront size={48} weight="duotone" />
          </div>
        )}
      </Link>
      <div className="p-5">
        <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">{p.category?.name || "Curated"}</p>
        <Link to={`/shop/${p.id}`}><h3 className="font-display text-xl text-white mt-1 leading-tight hover:text-[#F9E596]">{p.title}</h3></Link>
        {p.short_description && <p className="text-white/55 text-sm mt-1.5 line-clamp-2">{p.short_description}</p>}
        <div className="flex items-end justify-between mt-4">
          <div>
            <p className="font-display text-2xl text-white">{formatMoney(p.price)}</p>
            {p.delivery_fee > 0 && <p className="text-white/40 text-[11px]">+ {formatMoney(p.delivery_fee)} delivery</p>}
          </div>
          <button onClick={() => onAdd(p)} className="tsc-btn-gold text-[10px] flex items-center gap-2" data-testid={`shop-add-${p.id}`}>
            <ShoppingBag size={14} weight="bold" /> Add
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  useCart(); // re-render to keep "cart count" in sync
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [pr, fe, ca] = await Promise.all([
        api.get("/shop/products", { params: { category_id: cat || undefined, q: q || undefined } }),
        api.get("/shop/products", { params: { featured: true } }),
        api.get("/shop/categories"),
      ]);
      setProducts(pr.data);
      setFeatured(fe.data);
      setCategories(ca.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);

  const handleAdd = (p) => {
    addToCart(p.id, 1);
    toast.success(`${p.title} added to cart`);
  };

  const onSearch = (e) => { e.preventDefault(); load(); };

  const empty = !loading && products.length === 0;

  return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader />
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">The Shop</p>
            <h1 className="font-display text-4xl sm:text-5xl text-white mt-2">Curated for the Circle.</h1>
            <p className="text-white/55 mt-3 max-w-xl">A private members-friendly shop. Limited drops, lifestyle pieces, and partner exclusives.</p>
          </div>
          <Link to="/checkout" className="tsc-btn-outline flex items-center gap-2" data-testid="shop-go-cart">
            <ShoppingBag size={16} weight="duotone" /> Cart ({cartCount()})
          </Link>
        </div>

        {/* Featured */}
        {featured.length > 0 && (
          <div className="mt-10">
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Featured</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4" data-testid="shop-featured-grid">
              {featured.slice(0, 3).map((p) => <ProductCard key={p.id} p={p} onAdd={handleAdd} />)}
            </div>
          </div>
        )}

        {/* Filters */}
        <form onSubmit={onSearch} className="mt-12 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center" data-testid="shop-filters">
          <div className="flex items-center gap-2 tsc-input flex-1 max-w-md">
            <MagnifyingGlass size={16} className="text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="bg-transparent outline-none text-white text-sm flex-1" data-testid="shop-search-input" />
            <button className="text-[10px] tracking-luxe uppercase text-[#D4AF37]" data-testid="shop-search-submit">Search</button>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <button type="button" onClick={() => setCat("")} className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border whitespace-nowrap ${!cat ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/70"}`} data-testid="shop-cat-all">All</button>
            {categories.map((c) => (
              <button type="button" key={c.id} onClick={() => setCat(c.id)} className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border whitespace-nowrap ${cat === c.id ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/70"}`} data-testid={`shop-cat-${c.id}`}>{c.name}</button>
            ))}
          </div>
        </form>

        {/* Grid */}
        <div className="mt-8">
          {loading ? (
            <p className="text-white/55">Loading…</p>
          ) : empty ? (
            <div className="text-center py-20 border border-dashed border-white/10 rounded-md" data-testid="shop-empty">
              <Storefront size={36} className="text-white/30 mx-auto" weight="duotone" />
              <p className="text-white/60 mt-3">No products yet.</p>
              <p className="text-white/35 text-xs mt-1">Our team is curating the first drop.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-grid">
              {products.map((p) => <ProductCard key={p.id} p={p} onAdd={handleAdd} />)}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
